import { REACT_APP_PSB_BUSINESSID } from 'envs';
import { loginState } from 'global-stores';
import { useOutsideClick } from 'hooks';
import {
	CSSProperties,
	FC,
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import { fixedOrigin } from 'views/design-system';

/**
 * @Manish Define the props for the InboxDropdown component
 * */
interface IInboxDropdown {
	historyCallback: () => void; // Callback function for history action
	voidCallback: () => void; // Callback function for void action
	viewDocCallback: () => void; // Callback function for view doc
	correctCallback: () => void; // Callback function for correct action
	editRecipientCallback: (isViewOnly?: boolean) => void; // Callback function for edit recipient action
	showVoided: boolean; // Flag to determine whether to show the "Void" option
	showView: boolean;
	isHideInboxOptions?: boolean; // prop for hide void option and view option in dropdown
	action: string;
	inboxType?: 'multiSigner' | 'selfSign' | 'onboarding' | '';
	showCorrect?: boolean;
	showViewEdit?: boolean;
}

/**
 * @Manish Create the InboxDropdown functional component
 * */
export const InboxDropdown: FC<IInboxDropdown> = ({
	historyCallback,
	voidCallback,
	showVoided,
	viewDocCallback,
	correctCallback,
	editRecipientCallback,
	showView,
	isHideInboxOptions,
	action,
	inboxType,
	showCorrect,
	showViewEdit,
}) => {
	/**
	 * @manish State to track whether the dropdown popup is open or closed
	 * */
	const [isPopupOpen, setPopupOpen] = useState(false);
	const user = useRecoilValue(loginState);

	/**
	 * @Manish Create a ref for the dropdown element to handle clicks outside of it
	 * */
	const refPopup = useRef<HTMLDivElement>(null);
	useOutsideClick(refPopup, () => {
		setPopupOpen(false);
	});

	/**
	 * The open menu is `position: fixed`, placed from the button group's
	 * on-screen rect — same fix as the design-system Kebab.
	 *
	 * Hanging `absolute` below the button inside a table's scroll container
	 * made the last row's menu stretch that container: a scrollbar appeared
	 * and the menu had to be scrolled to (the Draft list, with one row, showed
	 * it every time). Fixed escapes every clipping ancestor; it drops down when
	 * the viewport has room and flips up when it does not.
	 */
	const menuRef = useRef<HTMLDivElement>(null);
	const [place, setPlace] = useState<CSSProperties>();

	useLayoutEffect(() => {
		const el = refPopup.current;
		const menu = menuRef.current;
		if (!isPopupOpen || !el || !menu) {
			setPlace(undefined);
			return;
		}
		const anchor =
			(el.closest('.inbox-dropdown__wrapper') as HTMLElement | null) ?? el;
		const rect = anchor.getBoundingClientRect();
		const origin = fixedOrigin(anchor);
		const needed = menu.offsetHeight + 8;
		const below = window.innerHeight - rect.bottom;
		const up = below < needed && rect.top > below;
		const top = up ? rect.top - 4 - menu.offsetHeight : rect.bottom + 4;
		setPlace({
			position: 'fixed',
			top: Math.max(8, top) - origin.top,
			left: rect.left - origin.left,
			width: rect.width,
			right: 'auto',
			bottom: 'auto',
			marginTop: 0,
			zIndex: 1000,
		});
	}, [isPopupOpen]);

	/* A fixed menu does not follow its row, so close it on scroll/resize rather
	   than leave it floating over the wrong row. */
	useEffect(() => {
		if (!isPopupOpen) return undefined;
		const close = () => setPopupOpen(false);
		window.addEventListener('scroll', close, true);
		window.addEventListener('resize', close);
		return () => {
			window.removeEventListener('scroll', close, true);
			window.removeEventListener('resize', close);
		};
	}, [isPopupOpen]);

	/**
	 * @Manish Callback function to toggle the visibility of the dropdown popup
	 * */
	const handlehello = useCallback(() => {
		setPopupOpen(pre => !pre);
	}, []);

	return (
		<div ref={refPopup} onClick={handlehello}>
			<div className="inbox-dropdown__icon">
				<i className="ri-arrow-down-s-fill" />
			</div>
			{/* @Manish Render the dropdown popup when it's open */}
			{isPopupOpen && (
				<div className="inbox-dropdown" ref={menuRef} style={place}>
					<div className="inbox-dropdown__container">
						{showView && (
							<div
								className="inbox-dropdown__menu-item"
								onClick={viewDocCallback}
							>
								View
							</div>
						)}
						{/* @Manish Display the "History" option with a callback */}
						{/* Arun Kumar : isHideInboxOptions is a prop for to hide histroy btn in dropdown if we are showing histro label   */}
						{/* {isHideInboxOptions &&
							(action === 'completed' ||
								action === 'voided' ||
								action === 'needToSign' || action === "processing") && (
								<div
									className="inbox-dropdown__menu-item"
									onClick={historyCallback}
								>
									Certificates
								</div>
						)} */}
						{((inboxType === 'onboarding' &&
							(action === 'completed' || action === 'processing')) ||
							(inboxType !== 'onboarding' && action !== 'editable')) && (
							<div
								className="inbox-dropdown__menu-item"
								onClick={historyCallback}
							>
								Certificates
							</div>
						)}
						{/* @Manish Display the "Void" option conditionally based on the showVoided flag */}
						{showVoided && !isHideInboxOptions && action !== 'editable' && (
							<div className="inbox-dropdown__menu-item" onClick={voidCallback}>
								Void
							</div>
						)}
						{showCorrect && inboxType !== 'onboarding' && (
							<Fragment>
								<div
									className="inbox-dropdown__menu-item"
									onClick={correctCallback}
								>
									Correct
								</div>
								<div
									className="inbox-dropdown__menu-item"
									onClick={() => editRecipientCallback(false)}
								>
									Edit Recipient
								</div>
							</Fragment>
						)}
						{showViewEdit && REACT_APP_PSB_BUSINESSID === user.business && (
							<div
								className="inbox-dropdown__menu-item"
								onClick={() => editRecipientCallback(true)}
							>
								Recipients access code
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
